import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useMemo } from 'react'

export interface ClientReminder {
  id: string
  user_id: string
  client_id: string
  project_id: string | null
  title: string
  description: string | null
  reminder_date: string
  reminder_time: string | null
  reminder_type: string
  is_completed: boolean
  created_at: string
  updated_at: string
}

export interface ClientReminderInsert {
  client_id: string
  project_id?: string | null
  title: string
  description?: string | null
  reminder_date: string
  reminder_time?: string | null
  reminder_type?: string
  is_completed?: boolean
}

export interface ClientReminderUpdate {
  id: string
  title?: string
  description?: string | null
  reminder_date?: string
  reminder_time?: string | null
  reminder_type?: string
  is_completed?: boolean
  project_id?: string | null
}

export function useClientReminders(clientId: string) {
  return useQuery({
    queryKey: ['client-reminders', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_reminders')
        .select('*')
        .eq('client_id', clientId)
        .order('reminder_date', { ascending: true })

      if (error) throw error
      return data as ClientReminder[]
    },
    enabled: !!clientId,
  })
}

export function useCreateClientReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (reminder: ClientReminderInsert) => {
      const { data: userData } = await supabase.auth.getUser()
      if (!userData.user) throw new Error('Usuário não autenticado')

      const { data, error } = await supabase
        .from('client_reminders')
        .insert({
          ...reminder,
          user_id: userData.user.id,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-reminders', variables.client_id] })
    },
  })
}

export function useUpdateClientReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: ClientReminderUpdate) => {
      const { data, error } = await supabase
        .from('client_reminders')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as ClientReminder
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['client-reminders', data.client_id] })
    },
  })
}

export function useDeleteClientReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
      const { error } = await supabase
        .from('client_reminders')
        .delete()
        .eq('id', id)

      if (error) throw error
      return { clientId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['client-reminders', data.clientId] })
    },
  })
}

export function useToggleReminderComplete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, is_completed }: { id: string; is_completed: boolean }) => {
      const { data, error } = await supabase
        .from('client_reminders')
        .update({ is_completed })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as ClientReminder
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['client-reminders', data.client_id] })
    },
  })
}

// Hook to fetch next reminder for all clients (for list views)
export function useAllClientsNextReminders() {
  return useQuery({
    queryKey: ['client-reminders', 'all-next'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0]
      
      const { data, error } = await supabase
        .from('client_reminders')
        .select('*')
        .eq('is_completed', false)
        .gte('reminder_date', today)
        .order('reminder_date', { ascending: true })
        .order('reminder_time', { ascending: true, nullsFirst: false })

      if (error) throw error

      // Group by client_id and get the first (next) reminder for each
      const nextByClient: Record<string, ClientReminder> = {}
      data?.forEach(reminder => {
        if (!nextByClient[reminder.client_id]) {
          nextByClient[reminder.client_id] = reminder as ClientReminder
        }
      })

      return nextByClient
    },
  })
}
